<template>
  <div class="model">
    <div class="model-loading" v-if="loading">
      <slot name="loading">加载模型中</slot>
    </div>
    <div class="model" ref="model"></div>
    <slot></slot>
    <slot name="anchor"></slot>
  </div>
</template>

<script>
import {
  addContextMenu,
  checkColorObject,
  createWebApp3D,
  createWebApp3DConfig,
  error,
  loadBimfaceSDK,
  log,
  registerClickInfo,
  registerModelAddedEvent,
  registerViewerAddedEvent,
} from '../utils/bimface';
import bus from '../utils/bus';

export default {
  name: 'bimface',
  props: {
    // 主视角
    HomeButton: {
      type: Boolean,
      default: true,
    },
    // 框选
    RectangleSelectButton: {
      type: Boolean,
      default: true,
    },
    // 测量
    MeasureButton: {
      type: Boolean,
      default: true,
    },
    // 剖切
    SectionButton: {
      type: Boolean,
      default: true,
    },
    // 漫游
    WalkButton: {
      type: Boolean,
      default: true,
    },
    // 地图
    MapButton: {
      type: Boolean,
      default: true,
    },
    // 构件详情
    PropertyButton: {
      type: Boolean,
      default: true,
    },
    // 设置
    SettingButton: {
      type: Boolean,
      default: true,
    },
    // 基本信息
    InformationButton: {
      type: Boolean,
      default: true,
    },
    // 全屏
    FullScreenButton: {
      type: Boolean,
      default: true,
    },
    effectMode: {
      type: String,
      default: 'performance',
    },
    MainToolbar: {
      type: Boolean,
      default: true,
    },
    ModelTree: {
      type: Boolean,
      default: true,
    },
    backgroundColor: [Array, Object],
    // Viewer3DConfig https://bimface.com/docs/model-viewer/v1/api-reference/Glodon.Bimface.Viewer.Viewer3DConfig.html
    viewerOptions: Object,
    // 摄像机状态
    cameraState: Object,
  },
  data() {
    return {
      viewTokenMap: new Map(),
      webApp3D: null,
      loading: true,
      drawableMng: null,
      anchorMng: null,
      materialContainer: null,
      animationContainer: null,
      viewer3D: null,
      toProvide: {
        viewer3D: null,
        webApp3D: null,
        modelData: {},
        model3D: {},
        drawableMng: null,
        anchorMng: null,
        materialContainer: null,
        animationContainer: null,
        externalObjMng: null,
      },
    };
  },
  provide() {
    return {
      bimface: this.toProvide,
    };
  },
  watch: {
    backgroundColor(newVal) {
      if (this.viewer3D) {
        let processBgColor = this.processBgColor(newVal);
        this.viewer3D.setBackgroundColor(
          processBgColor[0].color,
          processBgColor[1]?.color,
        );
      }
    },
    'viewerOptions.backgroundColor': function(newVal) {
      if (this.viewer3D) {
        let processBgColor = this.processBgColor(newVal);
        this.viewer3D.setBackgroundColor(
          processBgColor[0].color,
          processBgColor[1]?.color,
        );
      }
    },
    cameraState: function(newVal) {
      if (this.viewer3D) {
        this.viewer3D.setCameraStatus(newVal);
      }
    },
  },
  async mounted() {
    // 初始化模型数据
    await this.loadModelData();
    // 初始化Web3DApp
    this.webApp3D = this.initWeb3DApp(this.viewerOptions || {});
    this.viewer3D = this.webApp3D.getViewer();
    this.toProvide.viewer3D = this.viewer3D;
    this.toProvide.webApp3D = this.webApp3D;
    // 注册事件
    this.registerViewerAdded(this.viewer3D);
    this.registerModelLoaded(this.viewer3D);
    // 注册开发辅助工具
    this.registerDevTools(this.viewer3D);
    this.viewer3D.render();
  },
  methods: {
    registerDevTools(viewer3D) {
      // 开发环境下添加一些工具菜单
      // 增加鼠标右键监听事件
      viewer3D.addEventListener(
        Glodon.Bimface.Viewer.Viewer3DEvent.ContextMenu,
        function(objectData) {
          setTimeout(function() {
            // 添加获取相机状态菜单
            addContextMenu('获取相机状态', () => {
              log(viewer3D.getCameraStatus());
            });
            addContextMenu('获取相机状态(json)', () => {
              let json = JSON.stringify(viewer3D.getCameraStatus());
              json.replace(/\\"/g, '\uFFFF'); //U+ FFFF
              json = json.replace(/"([^"]+)":/g, '$1:').replace(/\uFFFF/g, '\\"');
              log(json);
            });
          }, 0);
        },
      );
      registerClickInfo(viewer3D, (objData) => {
        log(objData);
        this.$emit('click', objData);
      });
    },
    registerViewerAdded(viewer3D) {
      registerViewerAddedEvent(viewer3D, (viewer3D) => {
        log('viewer loaded', viewer3D);
        // 更新摄像机视角
        this.cameraState &&
        this.updateCameraState(this.viewer3D, this.cameraState);
        // 初始化标签容器
        this.initDrawableContainer(viewer3D);
        // 初始化锚点容器
        this.initAnchorContainer(viewer3D);
        this.initMaterialContainer(viewer3D);
        this.initAnimationContainer(viewer3D);
        this.initExternalObjectMng(viewer3D);
        this.$emit('viewAdded', viewer3D);
        bus.$emit('viewerAdded', viewer3D);
      });
    },
    registerModelLoaded(viewer3D) {
      registerModelAddedEvent(viewer3D, (modelId) => {
        let model3D = viewer3D.getModel(modelId);
        log('model loaded', modelId, model3D);
        this.loading = false;
        this.toProvide.model3D[modelId] = model3D;
        // 调用相应子组件的onModelLoaded方法
        for (const modelComp of this.getModelComps()) {
          let comp = modelComp.componentInstance;
          if (comp.modelId === modelId) {
            comp.onModelLoaded(model3D);
          }
        }
      });
    },
    initWeb3DApp(viewerOptions) {
      let viewerOptionsCopy = {...viewerOptions};
      if (viewerOptionsCopy.backgroundColor) {
        viewerOptionsCopy.backgroundColor = this.processBgColor(
          viewerOptionsCopy.backgroundColor,
        );
      } else if (this.backgroundColor) {
        viewerOptionsCopy.backgroundColor = this.processBgColor(
          this.backgroundColor,
        );
      }
      let webApp3DConfig = createWebApp3DConfig(
        Object.assign(
          {
            Buttons: this.getButtons(),
            Toolbars: this.getToolbars(),
            domElement: this.$refs.model,
          },
          viewerOptionsCopy,
        ),
      );
      return createWebApp3D(webApp3DConfig);
    },
    processBgColor(bgColor) {
      let viewerBackgroundColor = [];
      let backgroundColors = [];
      if (
        Array.isArray(bgColor) &&
        bgColor.every((item) => typeof item === 'object')
      ) {
        backgroundColors = backgroundColors.concat(bgColor);
        for (const bgColor of backgroundColors) {
          checkColorObject(bgColor);
          viewerBackgroundColor.push({
            ...bgColor,
            color: new Glodon.Web.Graphics.Color(...bgColor.color),
          });
        }
      } else {
        viewerBackgroundColor = [
          {
            color: new Glodon.Web.Graphics.Color(...bgColor),
          },
        ];
      }
      return viewerBackgroundColor;
    },
    async loadModelData() {
      for (const modelComp of this.getModelComps()) {
        let comp = modelComp.componentInstance;
        try {
          // log('加载模型数据')
          if (comp.fileId === undefined && comp.viewToken === undefined) {
            error('file-id或view-token必须有一个不为空');
          }
          let viewToken;
          if (comp.fileId) {
            viewToken = await this.getViewToken(comp.fileId);
          } else {
            viewToken = comp.viewToken;
          }
          let modelData = await loadBimfaceSDK(viewToken);
          this.toProvide.modelData[comp.fileId] = {
            viewToken,
            modelData,
          };
        } catch (err) {
          error(err);
        }
      }
    },
    async getViewToken(fileId) {
      if (this.viewTokenMap.has(fileId)) return this.viewTokenMap.get(fileId);
      let viewToken = await this.getViewTokenApi(fileId);
      this.viewTokenMap.set(fileId, viewToken);
      return viewToken;
    },
    getModelComps() {
      return this.$slots.default.filter((comp) => {
        return (
          comp.componentInstance &&
          typeof comp.componentInstance.isBimfaceModel === 'function' &&
          comp.componentInstance.isBimfaceModel()
        );
      });
    },
    updateCameraState(viewer3D, cameraState) {
      viewer3D.setCameraStatus(cameraState);
      viewer3D.recordCustomHomeview(cameraState);
    },
    initAnchorContainer(viewer3D) {
      let anchorManagerConfig =
        new Glodon.Bimface.Plugins.Anchor.AnchorManagerConfig();
      anchorManagerConfig.viewer = viewer3D;
      this.anchorMng = new Glodon.Bimface.Plugins.Anchor.AnchorManager(
        anchorManagerConfig,
      );
      this.toProvide.anchorMng = this.anchorMng;
    },
    initDrawableContainer(viewer3D) {
      let drawableContainerConfig =
        new Glodon.Bimface.Plugins.Drawable.DrawableContainerConfig();
      drawableContainerConfig.maxNum = 100;
      drawableContainerConfig.viewer = viewer3D;
      this.drawableMng = new Glodon.Bimface.Plugins.Drawable.DrawableContainer(
        drawableContainerConfig,
      );
      this.toProvide.drawableMng = this.drawableMng;
    },
    initExternalObjectMng(viewer3D) {
      this.toProvide.externalObjMng =
        new Glodon.Bimface.Plugins.ExternalObject.ExternalObjectManager(
          viewer3D,
        );
    },
    initMaterialContainer(viewer3D) {
      this.materialContainer =
        new Glodon.Bimface.Plugins.Material.MaterialContainer();
      this.toProvide.materialContainer = this.materialContainer;
    },
    initAnimationContainer(viewer3D) {
      this.animationContainer =
        new Glodon.Bimface.Plugins.Animation.AnimationContainer();
      this.toProvide.animationContainer = this.animationContainer;
    },
    getToolbars() {
      return ['MainToolbar', 'ModelTree'].filter((item) => {
        return this[item];
      });
    },
    getButtons() {
      return [
        'Home',
        'RectangleSelect',
        'Measure',
        'Section',
        'Walk',
        'Map',
        'Property',
        'Setting',
        'Information',
        'FullScreen',
      ].filter((item) => {
        return this[item + 'Button'];
      });
    },
  },
  activated() {
    this.viewer3D && this.viewer3D.render();
  },
  beforeDestroy() {
    log('卸载webApp3D');
    // this.webApp3D.destroy();
  },
};
</script>

<style lang="scss" scoped>
.model-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.model {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>
